import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'

import Quests from '../components/quests/Quests'
import {
  changeQuestState,
  changeQuestListState,
  createQuest,
  getQuests,
  resetQuestState,
  toggleQuest,
  updateQuest,
  resetQuestsList,
  selectQuest
} from '../store/actions/quest'
import {
  DEFAULT_CONTAINER_STATE,
  QUEST_PROCESS_STEP
} from '../constants/containerStates'
import CreateQuestForm from '../components/quests/CreateQuestForm'
import { checkValidations, formatValidator } from '../util/validation.helpers'
import { containsValues } from '../util/object.helpers'
import {
  buildQuestValidationRules,
  QUEST_STATUS_OPTIONS,
  questTableHeaders,
  questUpdateFieldsFormat
} from '../util/quest.helpers'
import { QUEST_PROFILE_DISPATCH } from '../store/reducers/quest/quest'
import { buildTableHeaders } from '../util/csv.helpers'
import { isEndDateInPast } from '../util/date.helpers'
import { getTokens } from '../store/actions/token'
import { isDashboardReady } from '../util/dashboard.helpers'
import { isTokenFullIntegration } from '../util/analytics/pointsBank.helpers'

const QuestsContainer = ({
  tokenProfile,
  dashboardSetupState,
  changeQuestState,
  changeQuestListState,
  createQuest,
  resetQuestState,
  getQuests,
  questListProfile,
  questProfile,
  toggleQuest,
  updateQuest,
  resetQuestsList,
  selectQuest,
  getTokens,
  ...restProps
}) => {
  const isDashboardSetupReady = isDashboardReady(dashboardSetupState)
  const isFullIntegration = isTokenFullIntegration(tokenProfile.type)

  const { t } = useTranslation()
  const [popupState, setPopupState] = useState({
    id: DEFAULT_CONTAINER_STATE.NONE,
    data: null
  })

  useEffect(() => {
    getTokens()
    return () => {
      resetQuestsList()
    }
  }, [])

  useEffect(() => {
    if (isDashboardSetupReady && isFullIntegration) {
      getQuests()
    }
  }, [isDashboardSetupReady, isFullIntegration])

  useEffect(() => {
    if (questProfile.saveSuccess) {
      handleNoPopup()
    }
  }, [questProfile.saveSuccess])

  const handleCreateNewQuest = () => {
    setPopupState({
      id: QUEST_PROCESS_STEP.CREATE_NEW
    })
  }

  const handleNoPopup = () => {
    resetQuestState()
    setPopupState({
      id: DEFAULT_CONTAINER_STATE.NONE
    })
    selectQuest(null)
  }

  const handleActivateQuest = (option, isActivate) => {
    const { endDate } = option
    if (isActivate && isEndDateInPast(endDate, null, true)) {
      handleEditQuest(option)
      changeQuestState(QUEST_PROFILE_DISPATCH.END_DATE_TEMP, '')
      changeQuestState(QUEST_PROFILE_DISPATCH.ERROR, {
        endDateTemp: formatValidator('error.end-date-later-than-start')
      })
    } else {
      selectQuest(option, true)
      toggleQuest(option.id, {
        status: isActivate
          ? QUEST_STATUS_OPTIONS.ACTIVE
          : QUEST_STATUS_OPTIONS.STOPPED
      })
    }
  }

  const handleEditQuest = (option) => {
    selectQuest(option, false)
    setPopupState({
      id: QUEST_PROCESS_STEP.CREATE_NEW
    })
  }

  const handleOnCancelPopup = () => {
    switch (popupState) {
      default:
        handleNoPopup()
    }
  }

  const handleOnSubmitPopup = () => {
    switch (popupState.id) {
      case QUEST_PROCESS_STEP.CREATE_NEW:
        const { media } = questProfile
        changeQuestState(QUEST_PROFILE_DISPATCH.ERROR, {})
        const errors = checkValidations(buildQuestValidationRules(questProfile))
        if (containsValues(errors)) {
          changeQuestState(QUEST_PROFILE_DISPATCH.ERROR, errors)
        } else {
          const questId = questListProfile.selected?.id
          if (questId) {
            const fieldsToUpdate = questUpdateFieldsFormat(
              questId,
              questProfile
            )
            if (media || Object.keys(fieldsToUpdate.data).length) {
              updateQuest(media, fieldsToUpdate)
            }
          } else {
            createQuest(media)
          }
        }
        break
      default:
        break
    }
  }

  const renderPopup = () => {
    switch (popupState.id) {
      case QUEST_PROCESS_STEP.CREATE_NEW:
        const isEdit = questListProfile.selected?.id
        return (
          <CreateQuestForm
            t={t}
            tokenProfile={tokenProfile}
            questProfile={questProfile}
            onClose={handleOnCancelPopup}
            onSubmit={handleOnSubmitPopup}
            dispatchAction={changeQuestState}
            title={isEdit ? t('quest.edit-quest') : t('quest.create-new-quest')}
            submitLabel={isEdit ? t('common.save') : t('quest.create-quest')}
          />
        )

      default:
        break
    }
  }

  return (
    <>
      <Quests
        {...restProps}
        t={t}
        tokenProfile={tokenProfile}
        questListProfile={questListProfile}
        isDashboardSetupReady={isDashboardSetupReady}
        dispatchAction={changeQuestListState}
        onCreateNew={handleCreateNewQuest}
        questTableProps={{
          tableHeaders: buildTableHeaders(
            t,
            questTableHeaders(tokenProfile.symbol)
          ),
          onActivate: handleActivateQuest,
          onEdit: handleEditQuest
        }}
        isFullIntegration={isFullIntegration}
      />
      {renderPopup()}
    </>
  )
}

const mapStateToProps = ({ dashboardReducer, tokensReducer, questReducer }) => {
  const { dashboardSetupProfile } = dashboardReducer
  const { tokenProfile } = tokensReducer
  const { questProfile, questListProfile } = questReducer
  return {
    dashboardSetupState: dashboardSetupProfile.dashboardSetupState,
    tokenProfile,
    questListProfile,
    questProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getTokens,
      changeQuestState,
      changeQuestListState,
      createQuest,
      resetQuestState,
      getQuests,
      updateQuest,
      toggleQuest,
      resetQuestsList,
      selectQuest
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(QuestsContainer)
